import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import { apiErrorText } from 'helper/apiErrorText';
import StyledInput from 'components/styledInput/StyledInput';
import { COMPANY_DELETE_REASONS } from 'constants/companyDeleteReasons';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyCompaniesChanged } from 'hooks/useCompaniesChanged';
import useText from 'hooks/useText';
import crmApi from 'server/crm/crmApi';

export const DELETE_COMPANY_MODAL = 'delete_company';

// The api refuses longer details (INVALID_DELETE_REASON).
export const DELETE_DETAILS_MAX = 500;

/**
 * « Archiver l'entreprise », whatever its stage (admin/crm/company/delete
 * archives): the red alert above the title, then why it goes (« Autre » wants
 * details). Its missions, timesheets and invoices stay; it comes back from
 * the console's « Entreprises archivées ». `modalData`: { companyId, recordName }.
 * Once archived, the list of the companies opens; a refusal is told by its
 * errorCode and the modal stays open.
 */
const ModalDeleteCompany = () => {
  const { toggle } = useModal();
  const { modalData } = UseContext();
  const { call } = useCrmContext();
  const { text, language } = useText('recordForm');
  const navigate = useNavigate();
  const notifyCompanies = useNotifyCompaniesChanged();
  const [{ companyId, recordName }] = useState(() => ({
    companyId: modalData?.companyId,
    recordName: modalData?.recordName ?? '',
  }));
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = Boolean(reason) && (reason !== 'other' || Boolean(details.trim()));

  const confirm = () => {
    if (!valid || busy) return;
    setBusy(true);
    call(crmApi.deleteCompany, { companyId, reason, details: details.trim() }).then(
      () => {
        toast.success(text('deleteSaved'));
        notifyCompanies();
        toggle();
        navigate('/company');
      },
      (error) => {
        setBusy(false);
        toast.error(apiErrorText(error, { language, text, group: 'deleteErrors' }));
      },
    );
  };

  return (
    <>
      <Modalheader
        hide={toggle}
        hideShow={!busy}
        icon="danger"
        description={
          <>
            <span className="font-medium text-gray-900">{recordName}</span> — {text('deleteWarning')}
          </>
        }
      >
        {text('deleteTitle')}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <StyledInput
            type="select"
            label={text('deleteReason')}
            list={[
              { value: '', label: text('deleteReasonPlaceholder') },
              ...COMPANY_DELETE_REASONS.map((one) => ({ value: one, label: text(`deleteReason_${one}`) })),
            ]}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <StyledInput
            type="textarea"
            label={text(reason === 'other' ? 'deleteDetailsRequired' : 'reasonDetails')}
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            maxLength={DELETE_DETAILS_MAX}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color={busy ? 'DISABLED' : 'SECONDARY'} disabled={busy} onClick={toggle}>
          {text('deleteCancel')}
        </Button>
        <Button size="LG" color={valid && !busy ? 'PRIMARY' : 'DISABLED'} disabled={!valid || busy} onClick={confirm}>
          {busy ? text('deleteBusy') : text('deleteCta')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalDeleteCompany;
